import { useEffect, useMemo, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Building2, Check, Globe, Layers } from 'lucide-react'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { propertiesApiClient } from '@/lib/api/properties.client'
import {
  directoryApiClient,
  type PropertyGroupWithMembers,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import type { Property } from '@super/shared-types'

const ALL_PROPERTIES_ID = '__all-properties__'

// One dropdown, two form fields: groups are listed first and select into
// `groupsName` (live references — membership resolves at call time),
// individual properties select into `name`. Matching is properties OR groups.
interface PropertyFilterFieldProps {
  name: string
  groupsName: string
  label?: string
  allLabel?: string
  allSublabel?: string
  buttonClassName?: string
}

type ItemData =
  | { kind: 'property'; property: Property }
  | { kind: 'group'; group: PropertyGroupWithMembers }
  | null

export function PropertyFilterField({
  name,
  groupsName,
  label = 'Properties',
  allLabel = 'All properties',
  allSublabel = 'No property restriction',
  buttonClassName = 'w-full',
}: PropertyFilterFieldProps) {
  const { control } = useFormContext()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [properties, setProperties] = useState<Property[]>([])
  const [propertiesLoaded, setPropertiesLoaded] = useState(false)
  const [groups, setGroups] = useState<PropertyGroupWithMembers[]>([])
  const [groupsLoaded, setGroupsLoaded] = useState(false)
  const { field, fieldState } = useController({ name, control })
  const { field: groupsField, fieldState: groupsFieldState } = useController({
    name: groupsName,
    control,
  })
  const value: string[] = Array.isArray(field.value) ? field.value : []
  const groupsValue: string[] = Array.isArray(groupsField.value)
    ? groupsField.value
    : []

  // Track whether the "All properties" pseudo-badge should be visible.
  // Default: yes (no restriction). User can dismiss it (X) or replace it by
  // selecting properties/groups. Hydration with an existing non-empty
  // selection auto-hides it.
  const [showAllBadge, setShowAllBadge] = useState(true)
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current) return
    if (value.length > 0 || groupsValue.length > 0) {
      setShowAllBadge(false)
      hydratedRef.current = true
    }
  }, [value.length, groupsValue.length])

  useEffect(() => {
    if (!currentCompany?.id) return
    let cancelled = false
    const fetchProperties = isSuperAdmin
      ? propertiesApiClient.getAllAdminPropertiesForCompany(currentCompany.id)
      : propertiesApiClient.getProperties()
    const fetchGroups = isSuperAdmin
      ? directoryAdminApiClient.listPropertyGroups(currentCompany.id)
      : directoryApiClient.listPropertyGroups()

    fetchProperties
      .then(result => {
        if (cancelled) return
        setProperties(Array.isArray(result) ? result : [])
        setPropertiesLoaded(true)
      })
      .catch(() => {
        // Leave propertiesLoaded false on transient fetch failures so the
        // prune effect doesn't silently wipe the user's current selection
        // when the network blips. The user keeps their data; we just can't
        // resolve names until the next successful load.
        if (cancelled) return
        setProperties([])
      })
    fetchGroups
      .then(result => {
        if (cancelled) return
        setGroups(Array.isArray(result) ? result : [])
        setGroupsLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        setGroups([])
      })
    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin])

  // Once properties/groups resolve, drop any form-value IDs that aren't in
  // the loaded lists (e.g. a property or group was deleted). Without this
  // prune, the UI could show "All properties" while the form still submits
  // stale IDs.
  useEffect(() => {
    if (!propertiesLoaded || value.length === 0) return
    const known = new Set(properties.map(p => p.id))
    const pruned = value.filter(id => known.has(id))
    if (pruned.length !== value.length) {
      field.onChange(pruned)
    }
  }, [propertiesLoaded, properties, value, field])

  useEffect(() => {
    if (!groupsLoaded || groupsValue.length === 0) return
    const known = new Set(groups.map(g => g.id))
    const pruned = groupsValue.filter(id => known.has(id))
    if (pruned.length !== groupsValue.length) {
      groupsField.onChange(pruned)
    }
  }, [groupsLoaded, groups, groupsValue, groupsField])

  const allItem = useMemo<SearchDropdownItem<ItemData>>(
    () => ({
      id: ALL_PROPERTIES_ID,
      label: allLabel,
      sublabel: allSublabel,
      icon: <Globe className="h-4 w-4" />,
      data: null,
      isExclusive: true,
    }),
    [allLabel, allSublabel],
  )

  const items = useMemo<SearchDropdownItem<ItemData>[]>(
    () => [
      allItem,
      ...groups.map(group => ({
        id: group.id,
        label: group.name,
        sublabel: `Group · ${group.propertyIds.length} ${group.propertyIds.length === 1 ? 'property' : 'properties'}`,
        icon: <Layers className="h-4 w-4" />,
        data: { kind: 'group', group } as ItemData,
      })),
      ...properties.map(property => ({
        id: property.id,
        label: property.name || property.externalId,
        sublabel: `${property.propertyAddress.addressCity}, ${property.propertyAddress.addressState}`,
        icon: <Building2 className="h-4 w-4" />,
        data: { kind: 'property', property } as ItemData,
      })),
    ],
    [allItem, groups, properties],
  )

  const selectedItems = useMemo(() => {
    if (value.length > 0 || groupsValue.length > 0) {
      return items.filter(
        item => value.includes(item.id) || groupsValue.includes(item.id),
      )
    }
    return showAllBadge ? [allItem] : []
  }, [allItem, items, showAllBadge, value, groupsValue])

  const handleSelect = (
    selection:
      | SearchDropdownItem<ItemData>
      | SearchDropdownItem<ItemData>[]
      | null,
  ) => {
    const next = Array.isArray(selection)
      ? selection
      : selection
        ? [selection]
        : []
    if (next.some(i => i.id === ALL_PROPERTIES_ID)) {
      setShowAllBadge(true)
      field.onChange([])
      groupsField.onChange([])
      return
    }
    if (next.length === 0) {
      setShowAllBadge(false)
      field.onChange([])
      groupsField.onChange([])
      return
    }
    setShowAllBadge(false)
    field.onChange(next.filter(i => i.data?.kind === 'property').map(i => i.id))
    groupsField.onChange(
      next.filter(i => i.data?.kind === 'group').map(i => i.id),
    )
  }

  const errorMessage =
    fieldState.error?.message ?? groupsFieldState.error?.message

  return (
    <FormItem>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <SearchDropdown<ItemData>
          items={items}
          selectedItems={selectedItems}
          onSelect={handleSelect}
          multiSelect
          placeholder={allLabel}
          searchPlaceholder="Search properties and groups..."
          buttonClassName={buttonClassName}
          emptyMessage="No properties found"
          renderItem={(item, isSelected) => (
            <>
              {item.icon}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.label}</div>
                {item.sublabel && (
                  <div className="text-xs text-muted-foreground truncate mt-1">
                    {item.sublabel}
                  </div>
                )}
              </div>
              {isSelected && <Check className="h-4 w-4 shrink-0" />}
            </>
          )}
        />
      </FormControl>
      {errorMessage && <FormMessage>{errorMessage}</FormMessage>}
    </FormItem>
  )
}
